import styled from '@emotion/styled';

import { spacing, borderRadius, shadows, transitions, typography } from '../tokens';

const spec = {
  border: '#595959',
  borderOpen: '#000FFF',
  borderFocus: '#000FFF',
  placeholder: '#8C8C8C',
  text: '#3D3D3D',
  chevron: '#262626',
  itemBorder: '#EEEEEE',
  itemHover: '#F5F5F5',
};

export type DropdownSize = 'md' | 'xl';
export type DropdownShape = 'default' | 'pill';

const sizeMap: Record<DropdownSize, { height: string; fontSize: string; padding: string; width?: string }> = {
  md: { height: '38px', fontSize: typography.fontSize.md, padding: `0 ${spacing['2xl']} 0 ${spacing.md}` },
  xl: { height: '48px', fontSize: typography.fontSize.sm, padding: `0 44px 0 32px`, width: '256px' },
};

export const DropdownWrapper = styled('div', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $fullWidth: boolean }>`
  display: inline-flex;
  flex-direction: column;
  position: relative;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

export const DropdownTrigger = styled('button', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $size: DropdownSize; $shape: DropdownShape; $open: boolean; $hasValue: boolean; $fullWidth: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  box-sizing: border-box;
  width: ${({ $fullWidth, $size }) => $fullWidth ? '100%' : (sizeMap[$size].width || 'auto')};
  height: ${({ $size }) => sizeMap[$size].height};
  padding: ${({ $size }) => sizeMap[$size].padding};
  background: #ffffff;
  border: 1.5px solid ${({ $open }) => ($open ? spec.borderOpen : spec.border)};
  border-radius: ${({ $shape }) => ($shape === 'pill' ? '999px' : borderRadius.md)};
  cursor: pointer;
  font-family: ${typography.fontFamily.primary};
  font-size: ${({ $size }) => sizeMap[$size].fontSize};
  font-weight: ${typography.fontWeight.regular};
  color: ${({ $hasValue }) => ($hasValue ? spec.text : spec.placeholder)};
  transition: border-color ${transitions.fast};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus-visible {
    outline: 2px solid ${spec.borderFocus};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ChevronWrap = styled('span', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  color: ${spec.chevron};
  transition: transform ${transitions.base};
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  pointer-events: none;
  margin-left: ${spacing.sm};

  svg {
    width: 16px;
    height: 16px;
  }
`;

/* position/top/left/width/zIndex são passados inline pelo Portal */
export const DropdownList = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
  background: #ffffff;
  border: 1px solid ${spec.itemBorder};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.lg};
  max-height: 280px;
  overflow-y: auto;
`;

export const DropdownItem = styled('li', {
  shouldForwardProp: (p) => !p.toString().startsWith('$'),
})<{ $focused: boolean; $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.md};
  font-weight: ${({ $selected }) =>
    $selected ? typography.fontWeight.bold : typography.fontWeight.regular};
  color: ${({ $disabled }) => ($disabled ? spec.placeholder : spec.text)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: ${({ $focused }) => ($focused ? spec.itemHover : 'transparent')};
  border-bottom: 1px solid ${spec.itemBorder};
  transition: background ${transitions.fast};
  user-select: none;

  &:last-child {
    border-bottom: none;
  }
`;

export const DropdownLabel = styled('label')`
  display: block;
  margin-bottom: ${spacing.xs};
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  color: ${spec.text};
`;
